/**
 * Typed client for the plugin import API.
 *
 * The plugin import/management flow was a Convex surface on the mcpjam
 * backend (`plugins:*`, `pluginsNode:*`). sphinx has no equivalent endpoint
 * yet: the query hooks resolve to `undefined` (the same "skipped" state the
 * flag-off path always produced) and every imperative action rejects with
 * `PluginApiError("PLUGINS_DISABLED")`. Types and hook names are unchanged so
 * the plugin UI components compile and render their empty states.
 */

import {
  PluginApiError,
  type PluginCommitResult,
  type PluginDetail,
  type PluginImportRow,
  type PluginInspectResult,
  type PluginRuntimePreview,
  type PluginSetupStatus,
  type PluginSummary,
  type PluginVersionDetail,
} from "@/lib/plugins/plugin-api-types";

// ---------------------------------------------------------------------------
// Query hooks — always "skipped" (no backend).
// ---------------------------------------------------------------------------

export function usePluginImport(
  _importId: string | null | undefined,
): PluginImportRow | undefined {
  return undefined;
}

export function useProjectPlugins(
  _projectId: string | null | undefined,
): PluginSummary[] | undefined {
  return undefined;
}

export function useProjectPlugin(
  _pluginId: string | null | undefined,
): PluginDetail | undefined {
  return undefined;
}

export function usePluginVersion(
  _pluginVersionId: string | null | undefined,
): PluginVersionDetail | undefined {
  return undefined;
}

export function usePluginSetupStatus(
  _pluginVersionId: string | null | undefined,
): PluginSetupStatus | undefined {
  return undefined;
}

export function usePluginRuntimePreview(
  _projectId: string | null | undefined,
  _pluginVersionIds: string[] | null | undefined,
): PluginRuntimePreview | undefined {
  return undefined;
}

// ---------------------------------------------------------------------------
// Imperative actions.
// ---------------------------------------------------------------------------

export interface StartPluginImportArgs {
  projectId: string;
  /** The bundle ZIP (from `buildPluginZip` or a user-picked .zip file). */
  bundle: Blob | Uint8Array;
  /** Display label for the source. */
  sourceLabel?: string;
  /** Called with the new import id the moment `createImport` resolves. */
  onImportCreated?: (importId: string) => void;
}

export interface StartPluginImportResult {
  importId: string;
  /** Result of the inspect action (`preview_ready` or `failed`). */
  inspect: PluginInspectResult;
}

export interface PluginImportActions {
  createImport: (args: {
    projectId: string;
    bundleStorageId: string;
    sourceLabel?: string;
  }) => Promise<{ importId: string }>;
  inspectImport: (importId: string) => Promise<PluginInspectResult>;
  commitImport: (
    importId: string,
    options?: { setActive?: boolean },
  ) => Promise<PluginCommitResult>;
  startImport: (
    args: StartPluginImportArgs,
  ) => Promise<StartPluginImportResult>;
}

const disabled = async (..._args: unknown[]): Promise<never> => {
  throw new PluginApiError(
    "PLUGINS_DISABLED",
    "Plugin import is not available in sphinx.",
  );
};

const IMPORT_ACTIONS: PluginImportActions = {
  createImport: disabled,
  inspectImport: disabled,
  commitImport: disabled,
  startImport: disabled,
};

export function usePluginImportActions(): PluginImportActions {
  return IMPORT_ACTIONS;
}

export interface PluginManagementActions {
  /** Reversible kill switch (`plugins.setEnabled`). */
  setEnabled: (pluginId: string, enabled: boolean) => Promise<void>;
  /** Point the plugin at a different ready version. */
  activateVersion: (pluginId: string, pluginVersionId: string) => Promise<void>;
  /** Soft uninstall. */
  softDeletePlugin: (pluginId: string) => Promise<void>;
  /** Undo a soft uninstall. */
  restorePlugin: (pluginId: string) => Promise<void>;
}

const MANAGEMENT_ACTIONS: PluginManagementActions = {
  setEnabled: disabled,
  activateVersion: disabled,
  softDeletePlugin: disabled,
  restorePlugin: disabled,
};

export function usePluginManagementActions(): PluginManagementActions {
  return MANAGEMENT_ACTIONS;
}
